import { useId, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/provider';
import { ApiError } from '@/api/http';
import { queryKeys } from '@/api/queryKeys';
import type { MessageDetail, RemoteContentRule, RemoteMode } from '@/api/types';
import './remote-content.css';

/**
 * The reading pane's remote-content bar (ADR 0018, plan/12 §1.1).
 *
 * ## The cost, stated in the copy
 *
 * Loading a remote image is a network request to the sender's server, made
 * because this message was opened. It confirms the address is live and it leaks
 * the read time. Proxying it through our backend hides *who* and *from which
 * device* — the reader's IP, user agent and cookies never leave — but it cannot
 * hide *that* and *when*, and for a single-owner deployment "someone at this
 * server opened it" is "the owner opened it". The bar says so before offering
 * the button, because a UI that offered "Load images" with no sentence attached
 * would be hiding the only part of the decision that matters.
 *
 * ## Trackers
 *
 * A scored tracker is removed by the server, counted, and **never fetched under
 * any rule** — including a global "always load". So there is deliberately no
 * affordance anywhere in this component that could imply otherwise: no "load
 * these too", no per-tracker override, and the "always load" buttons are
 * labelled about *images*, never about everything. The tracker count is
 * reported as a thing that already happened, in the past tense.
 */

export interface RemoteContentBarProps {
  message: MessageDetail;
  accountId: string;
  /** The mode currently requested for this message, if the user chose one. */
  mode: RemoteMode | undefined;
  onModeChange: (mode: RemoteMode | undefined) => void;
  /**
   * True in the Junk role folder, where persistence is not offered at all
   * (doc 06 §4): "always load from this sender" for mail already classified as
   * junk is a grant handed to exactly the senders who should not have one.
   */
  isJunk?: boolean;
  /** Persisted rules, so "Stop always loading" can find the row to delete. */
  rules?: readonly RemoteContentRule[];
  /**
   * The message is an email attached to another (ADR 0092). Its sender can
   * never be verified -- whoever attached it wrote every header -- so the
   * server never applies a sender rule to it automatically, and no "always
   * load from" is offered here: it would be a rule this view can never use.
   */
  attached?: boolean;
}

function domainOf(email: string): string {
  const at = email.lastIndexOf('@');
  return at < 0 ? email : email.slice(at + 1);
}

export function RemoteContentBar({
  message,
  accountId,
  mode,
  onModeChange,
  isJunk = false,
  rules,
  attached = false,
}: RemoteContentBarProps) {
  const api = useApi();
  const queryClient = useQueryClient();
  const menuId = useId();
  const [menuOpen, setMenuOpen] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const info = message.remoteContent;
  const address = message.from.email;
  const domain = domainOf(address);

  // A rule changes what the server makes of every message it matches, so the
  // cached ones are stale too: reopened, a message fetched before the rule
  // would still say "blocked" and offer to save it again. Not awaited: the
  // open message is about to be re-requested under the reader's own choice.
  const invalidateRules = async (): Promise<void> => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.account(accountId).messageScope() });
    await queryClient.invalidateQueries({
      queryKey: queryKeys.account(accountId).remoteContentRules(),
    });
  };

  const addRule = useMutation({
    // 409 is "that rule already exists": saved on another device, or before
    // this copy of the message was cached. That is what the click asked for.
    mutationFn: (kind: 'sender' | 'domain') =>
      api.remoteContent
        .addRule(accountId, { kind, value: kind === 'sender' ? address : domain })
        .catch((cause: unknown) => {
          if (!(cause instanceof ApiError && cause.status === 409)) throw cause;
        }),
    onSuccess: async () => {
      setError(undefined);
      setMenuOpen(false);
      await invalidateRules();
      onModeChange('load');
    },
    // The server owns what counts as a valid value; its sentence is the
    // specific one, so it is shown rather than replaced with a generic line.
    onError: (cause: Error) => setError(cause.message),
  });

  const removeRule = useMutation({
    mutationFn: (ruleId: string) => api.remoteContent.removeRule(accountId, ruleId),
    onSuccess: async () => {
      setError(undefined);
      await invalidateRules();
      onModeChange('block');
    },
    onError: (cause: Error) => setError(cause.message),
  });

  // A backend that has not shipped `remote_content` yet is handled by the
  // caller's passive notice, not here: with no counts and no proxy behind it,
  // every button this component draws would be a button that cannot work.
  if (info === undefined) return null;

  const trackers = info.trackersRemoved;
  const trackerSentence =
    trackers === 1 ? '1 tracking image was removed.' : `${trackers} tracking images were removed.`;

  /* ------------------------------------------------------------- loaded */

  if (info.mode === 'loaded') {
    const rule = info.appliedRule;
    const matching = rule
      ? rules?.find((row) => row.kind === rule.kind && row.value === rule.value)
      : undefined;

    return (
      <div className="remote-bar" data-state="loaded">
        <p className="remote-bar__text">
          {rule
            ? `Images loaded because you always load from ${rule.kind === 'all' ? 'everyone' : rule.value}.`
            : `Images loaded.${trackers > 0 ? ` ${trackers} tracking ${trackers === 1 ? 'image was' : 'images were'} not.` : ''}`}
        </p>
        {rule && trackers > 0 && (
          <p className="remote-bar__detail muted">
            {trackerSentence} Tracking images are never loaded, even for senders you always allow.
          </p>
        )}
        <div className="remote-bar__actions">
          {rule && matching ? (
            <button
              type="button"
              className="btn"
              disabled={removeRule.isPending}
              onClick={() => removeRule.mutate(matching.id)}
            >
              Stop always loading
            </button>
          ) : (
            <button type="button" className="btn" onClick={() => onModeChange('block')}>
              Block again
            </button>
          )}
        </div>
        {error !== undefined && (
          <p className="remote-bar__error" role="alert">
            {error}
          </p>
        )}
      </div>
    );
  }

  /* ------------------------------------------------------------ blocked */

  const blocked = info.blocked;

  // Trackers only: there is genuinely nothing to load, so there is no button.
  // An enabled "Load images" here would do nothing and teach the user that the
  // button is a lie.
  if (blocked === 0) {
    if (trackers === 0) return null;
    return (
      <div className="remote-bar" data-state="trackers">
        <p className="remote-bar__text">{trackerSentence} There is nothing else to load.</p>
      </div>
    );
  }

  const unverified = info.appliedRule !== undefined && !info.appliedRule.verified;

  return (
    <div className="remote-bar" data-state="blocked">
      <p className="remote-bar__text">
        {blocked} {blocked === 1 ? 'image' : 'images'} {blocked === 1 ? 'wasn' : 'weren'}
        &rsquo;t loaded. Loading {blocked === 1 ? 'it' : 'them'} tells the sender this message was
        opened.
      </p>
      <p className="remote-bar__detail muted">
        It also tells them roughly when it was read. Images are fetched through this server, so the
        sender does not see your device or location.
        {trackers > 0 ? ` ${trackerSentence} Those are never loaded.` : ''}
      </p>
      {/*
        The server ignores every "always load" rule in Junk, so a sender the
        user trusts elsewhere still arrives blocked here. Said, so the blocked
        state is not mistaken for a rule that stopped working. The one-time
        Load images below is unaffected.
      */}
      {isJunk && (
        <p className="remote-bar__detail muted" data-testid="remote-bar-junk">
          Images are never loaded automatically in Junk, even from senders you always allow.
        </p>
      )}
      {attached && !unverified && (
        <p className="remote-bar__detail muted">
          Images in an attached email are only loaded when you ask: who sent it can&rsquo;t be
          verified.
        </p>
      )}
      {unverified && (
        <p className="remote-bar__detail muted">
          You always load from {info.appliedRule?.value}, but this message couldn&rsquo;t be verified
          as coming from them.
        </p>
      )}
      <div className="remote-bar__actions">
        {/*
          Disabled while a load is already requested and the re-rendered body
          has not come back yet: the bar still says "blocked" because that is
          what the *server's* last answer said, and a second click would mint a
          second request for the same decision.
        */}
        <button
          type="button"
          className="btn btn--primary"
          disabled={mode === 'load'}
          onClick={() => onModeChange('load')}
        >
          Load images
        </button>

        {/*
          Junk offers no persistence at all — not a disabled button, nothing. Nor
          does an attached email. Nor a message whose rule is already saved and
          was not applied: saving it again could only fail as a duplicate.
        */}
        {!isJunk && !attached && !unverified && (
          <div className="remote-bar__split">
            <button
              type="button"
              className="btn"
              disabled={addRule.isPending}
              onClick={() => addRule.mutate('sender')}
            >
              Always load from {address}
            </button>
            <button
              type="button"
              className="btn btn--icon remote-bar__more"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              aria-controls={menuId}
              aria-label={`More always-load options for ${address}`}
              onClick={() => setMenuOpen((open) => !open)}
            >
              <span aria-hidden="true">▾</span>
            </button>
            {menuOpen && (
              <div className="remote-bar__menu" id={menuId} role="menu">
                <button
                  type="button"
                  role="menuitem"
                  className="remote-bar__menu-item"
                  disabled={addRule.isPending}
                  onClick={() => addRule.mutate('domain')}
                >
                  Always load from @{domain}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      {error !== undefined && (
        <p className="remote-bar__error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
